// @flow
import type { Saga } from 'redux-saga';
import { all, call, put, takeLatest, takeEvery, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { push } from 'connected-react-router';

import { getRequestedInvoices, getAssociatedProviderInvoices, getAccountInvoices, getUserRut } from '../selectors';
import { InvoiceApi, PurchaseOrderApi } from '../../api';
import invoiceActions from './actions';
import { sortList, paymentStates } from '../../helpers';
import {
  getInvoicesTypes,
  getAccountInvoicesTypes,
  getAssociatedInvoicesTypes,
  getRequestedInvoicesTypes,
  getUnassociatedInvoices as getUnassociatedInvoicesTypes,
  sortInvoicesTypes,
  getNotifTypes,
  updateNotifTypes,
  updateAllNotifTypes,
  getReceiptDocumentTypes,
  deleteInvoicesTypes
} from './types';

export function* getInvoiceRequestSaga({ payload }: { payload: Object }): Saga {
  try {
    const { rut, dateFrom, dateTo, invoiceNumber } = payload;
    const data = yield call(InvoiceApi.getInvoicesList, {
      rut,
      dateFrom,
      dateTo,
      invoiceNumber
    });

    if (data.success) {
      const { invoices } = data;

      // Force USD to test selectable
      // for (let i = 0; i < invoices.length; i++) {
      //   if (i % 2 === 0) {
      //     invoices[i].coin = 'USD';
      //   }
      // }

      yield put(invoiceActions.getInvoicesSuccess({ invoices }));
    } else {
      yield put(invoiceActions.getInvoicesFailed(data.message));
      yield call(toast.error, data.message);
    }
  } catch (error) {
    yield put(invoiceActions.getInvoicesFailed('Ocurrió un error al intentar obtener las facturas electrónicas.'));
  }
}

export function* getAssociatedInvoiceRequestSaga({ payload }: { payload: Object }): Saga {
  try {
    const { dateFrom, dateTo, purchaseOrderId, orderType, invoiceNumber, paymentState } = payload;

    const userRut = yield select(getUserRut);
    const data = yield call(InvoiceApi.getInvoicesListFromProvider, {
      dateFrom,
      dateTo,
      purchaseOrderId,
      orderType,
      invoiceNumber,
      paymentState,
      rut: userRut
    });

    if (data.success) {
      const { invoices } = data;
      yield put(invoiceActions.getAssociatedInvoicesSuccess({ associatedInvoices: invoices }));
    } else {
      yield put(invoiceActions.getAssociatedInvoicesFailed(data.message));
    }
  } catch (error) {
    yield put(
      invoiceActions.getAssociatedInvoicesFailed('Ocurrió un error al intentar obtener las facturas asociadas.')
    );
  }
}

export function* getAccountInvoicesSaga({ payload }: { payload: any }): Saga {
  try {
    const { dateFrom, dateTo, purchaseOrderId, orderType, invoiceNumber, paymentState } = payload;
    const userRut = yield select(getUserRut);

    let notAssociatedInvoices = [];
    let associatedInvoices = [];
    let failed = false;

    if (paymentState === paymentStates.UNASSOCIATED_INVOICES || paymentState === paymentStates.ALL_INVOICES) { 
      const dataNotAssociated = yield call(InvoiceApi.getInvoicesList, {
        dateFrom,
        dateTo,
        rut: userRut
      });

      if (dataNotAssociated.success) { 
        notAssociatedInvoices = [...dataNotAssociated.invoices];
      } else { failed = true };
    }
    if (paymentState !== paymentStates.UNASSOCIATED_INVOICES) {
      let paymentStateAux = paymentState;
      if (paymentState === paymentStates.ALL_INVOICES) {
        paymentStateAux = '';
      }
      const dataAssociated = yield call(InvoiceApi.getInvoicesListFromProvider, {
        dateFrom,
        dateTo,
        purchaseOrderId,
        orderType,
        invoiceNumber,
        paymentState: paymentStateAux,
        rut: userRut
      });

      if (dataAssociated.success) { 
        associatedInvoices = [...dataAssociated.invoices];
      } else { failed = true };
    }

    if (!failed) {
      const invoices = [
        ...associatedInvoices.map(aInv => ({
          ...aInv,
          invType: 'ASSOCIATED_INVOICE'
        })),
        ...notAssociatedInvoices.map(accInv => ({ 
          ...accInv, 
          invType: paymentStates.UNASSOCIATED_INVOICES }))
      ];
      yield put(invoiceActions.getAccountInvoicesSuccess({ accountInvoices: invoices }));
    } else {
      yield put(invoiceActions.getAccountInvoicesFailed('Ocurrió un error al intentar obtener las facturas.'));
    }
  } catch (error) {
    yield put(invoiceActions.getAccountInvoicesFailed('Ocurrió un error al intentar obtener las facturas.'));
  }
}

export function* getUnassociatedInvoices({ payload }: { payload: any }): Saga {
  try {
    const { dateFrom, dateTo } = payload;

    const userRut = yield select(getUserRut);

    const dataNotAssociated = yield call(InvoiceApi.getInvoicesList, {
      dateFrom,
      dateTo,
      rut: userRut
    });
    if (dataNotAssociated.success) {
      const { invoices } = dataNotAssociated;
      yield put(invoiceActions.getUnassociatedInvoicesSuccess({ unassociatedInvoices: invoices }));
    } else {
      yield put(invoiceActions.getUnassociatedInvoicesFailed(dataNotAssociated.message));
    }
  } catch (error) {
    yield put(invoiceActions.getUnassociatedInvoicesFailed('Ocurrió un error al intentar obtener las facturas.'));
  }
}

export function* getRequestedInvoiceRequestSaga({ payload }: { payload: Object }): Saga {
  try {
    const { dateFrom, dateTo, purchaseOrderId, orderType, invoiceNumber, sinisterNumber, paymentState, providerRut } = payload;
    const data = yield call(InvoiceApi.getAdminRequestedInvoicesList, {
      dateFrom,
      dateTo,
      purchaseOrderId,
      orderType,
      invoiceNumber,
      sinisterNumber,
      paymentState,
      providerRut
    });

    if (data.success) {
      const { requestedInvoices } = data;
      yield put(invoiceActions.getRequestedInvoicesSuccess({ requestedInvoices }));
    } else {
      yield put(invoiceActions.getRequestedInvoicesFailed(data.message));
    }
  } catch (error) {
    yield put(
      invoiceActions.getRequestedInvoicesFailed(
        'Ocurrió un error al intentar obtener las facturas con pagos solicitados.'
      )
    );
  }
}

export function* sortRequestedInvoicesSaga({ payload }: { payload: Object }): Saga {
  const list = yield select(getRequestedInvoices);
  const { type, key, order } = payload;
  yield put(invoiceActions.getRequestedInvoicesSuccess({ requestedInvoices: sortList(type, key, order, list) }));
}

export function* sortAssociatedInvoicesSaga({ payload }: { payload: Object }): Saga {
  const list = yield select(getAssociatedProviderInvoices);
  const { type, key, order } = payload;
  yield put(invoiceActions.getAssociatedInvoicesSuccess({ associatedInvoices: sortList(type, key, order, list) }));
}

export function* sortAccountInvoicesSaga({ payload }: { payload: Object }): Saga {
  const list = yield select(getAccountInvoices);
  const { type, key, order } = payload;
  yield put(invoiceActions.getAccountInvoicesSuccess({ accountInvoices: sortList(type, key, order, list) }));
}

export function* getNotifRequestSaga({ payload }: { payload: Object }): Saga {
  try {
    const { providerRut } = payload;
    const data = yield call(InvoiceApi.getNotificationsList, {
      providerRut
    });

    if (data.success) {
      const { requestedNotifications } = data;
      yield put(invoiceActions.getNotificationsSuccess({ requestedNotifications }));
    } else {
      yield put(invoiceActions.getInvoicesFailed(data.message));
      yield call(toast.error, data.message);
    }
  } catch (error) {
    yield put(invoiceActions.getInvoicesFailed('Ocurrió un error al intentar obtener las notificaciones.'));
  }
}

export function* setNotifReadSaga({ payload }: { payload: Object }): Saga {
  try {
    const { providerRut, orderId, invoiceNumber } = payload;

    const data = yield call(InvoiceApi.setNotificationRead, {
      providerRut,
      orderId,
      invoiceNumber
    });

    yield put(push(`/orden/${orderId}`));
  } catch (error) {
    yield put(invoiceActions.getInvoicesFailed('Ocurrió un error al actualizar la notificación.'));
  }
}

export function* setAllNotifReadSaga({ payload }: { payload: Object }): Saga {
  try {
    const { providerRut, orderId, invoiceNumber, last } = payload;

    const data = yield call(InvoiceApi.setNotificationRead, {
      providerRut,
      orderId,
      invoiceNumber
    });
  } catch (error) {
    yield put(invoiceActions.getInvoicesFailed('Ocurrió un error al actualizar la notificación.'));
  }
}

export function* getReceiptDocumentRequestSaga({ payload }: { payload: Object }): Saga {
  try {
    const { userCode, authorizationNumber, invoiceNumber } = payload;

    const data = yield call(InvoiceApi.getReceiptDocument, {
      userCode,
      authorizationNumber
    });

    if (data) {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Resguardo ${invoiceNumber}.pdf`);
      if (document.body) document.body.appendChild(link);
      link.click();

      yield call(toast.success, 'El PDF del Resguardo se descargó con éxito.');
    } else {
      yield call(toast.error, 'No se pudo obtener el PDF del Resguardo.');
    }
  } catch (error) {
    yield call(toast.error, 'No se pudo obtener el PDF del Resguardo.');
  }
}

export function* deleteInvoicesRequestSaga({ payload }: { payload: Object }): Saga {
  try {
    const { invoiceIds, callback } = payload;
    let allOk = true; let data; let errorMsg;

    for(let i = 0; i < invoiceIds.length; i++) {
      data = yield call(PurchaseOrderApi.deleteAssociatedInvoiceById, invoiceIds[i]);
      allOk = allOk && data.success;
      if (!errorMsg && !data.success) errorMsg = data.message;
    }

    const successMsg = invoiceIds.length > 1 ?
      'Se borraron correctamente las facturas' :
      'Se borró correctamente la factura';

    if (allOk) {
      yield put(invoiceActions.deleteInvoicesSuccess());
      yield call(toast.success, successMsg);
    } else {
      yield put(invoiceActions.deleteInvoicesFailed(errorMsg));
      yield call(toast.error, 'Ocurrió un error al intentar eliminar una factura.');
    }
    if (callback) yield call(callback);

  } catch (error) {
    yield put(
      invoiceActions.deleteInvoicesFailed('Ocurrió un error al intentar eliminar una factura.')
    );
  }
}

export default function* invoiceSaga(): Saga {
  yield takeLatest(getInvoicesTypes.REQUEST, getInvoiceRequestSaga);
  yield takeLatest(getRequestedInvoicesTypes.REQUEST, getRequestedInvoiceRequestSaga);
  
  //yield takeLatest(getAssociatedInvoicesTypes.REQUEST, getAssociatedInvoiceRequestSaga);
  //yield takeLatest(getUnassociatedInvoicesTypes.REQUEST, getUnassociatedInvoices);
  yield takeLatest(getAccountInvoicesTypes.REQUEST, getAccountInvoicesSaga);

  yield takeLatest(sortInvoicesTypes.SORT_REQUESTED, sortRequestedInvoicesSaga);
  yield takeLatest(sortInvoicesTypes.SORT_ASSOCIATED, sortAssociatedInvoicesSaga);
  yield takeLatest(sortInvoicesTypes.SORT_ACCOUNT, sortAccountInvoicesSaga);
  yield takeLatest(getNotifTypes.REQUEST, getNotifRequestSaga);
  yield takeLatest(updateNotifTypes.REQUEST, setNotifReadSaga);
  yield takeEvery(updateAllNotifTypes.REQUEST, setAllNotifReadSaga);
  yield takeEvery(getReceiptDocumentTypes.REQUEST, getReceiptDocumentRequestSaga);
  yield takeLatest(deleteInvoicesTypes.DELETE_REQUEST, deleteInvoicesRequestSaga);
}
